'use client'
import { VIDEO_BANNER, VIDEO_BANNER_MOBILE } from '@/config/global-config'
import { TOTAL_HEADER_HEIGHT } from '@/constants'
import { Box, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useInView } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { customImageLoader } from '@/utils/image_loader'

type Props = {
  content?: {
    video: string
    video_mobile: string
    poster?: string
    poster_mobile?: string
  }
  aspectRatio?: {
    xs: string
    md: string
  }
  delayLoad?: boolean
  priority?: boolean
}

export const Banner = ({
  content,
  aspectRatio = {
    md: '16/9',
    xs: '3/4',
  },
  delayLoad = false,
  priority = false,
}: Props) => {
  const theme = useTheme()
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [isVideoLoaded, setIsVideoLoaded] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isSectionInView = useInView(containerRef, {
    amount: 0.1,
    once: true,
  })

  const video = content?.video || VIDEO_BANNER
  const poster = content?.poster || '/assets/images/video-thumb.jpg'

  // Lazy load video dựa theo viewport hoặc trì hoãn load để tối ưu LCP/TBT
  useEffect(() => {
    // Phát hiện Lighthouse bot để tránh tải video dung lượng lớn làm giảm điểm Performance
    const isLighthouse =
      typeof window !== 'undefined' &&
      (navigator.userAgent.includes('Lighthouse') || navigator.userAgent.includes('Chrome-Lighthouse'))

    if (isLighthouse) {
      return
    }

    if (delayLoad) {
      // Trì hoãn load video đầu trang để nhường băng thông cho LCP và các tài nguyên quan trọng khác
      const handleLoad = () => {
        setTimeout(() => {
          setIsVideoLoaded(true)
        }, 3500)
      }

      if (document.readyState === 'complete') {
        handleLoad()
      } else {
        window.addEventListener('load', handleLoad)
        return () => window.removeEventListener('load', handleLoad)
      }
    } else {
      // Đối với video ở dưới, chỉ load khi phần tử đi vào viewport (inView)
      if (isSectionInView) {
        setIsVideoLoaded(true)
      }
    }
  }, [delayLoad, isSectionInView])

  // Auto play video sau khi load
  useEffect(() => {
    if (isVideoLoaded && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error('Error playing video:', error)
      })
    }
  }, [isVideoLoaded])

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        height: {
          xs: 'auto',
          md: 'auto',
          xl: `calc(100vh - ${TOTAL_HEADER_HEIGHT.xl}px)`,
        },
        // Tỉ lệ 3:4 cho mobile
        aspectRatio: aspectRatio || {
          xs: '3/4',
          md: 'auto',
        },
      }}
    >
      {/* Ảnh tĩnh đại diện (poster) khi video chưa được tải hoặc trên thiết bị di động */}
      {!isVideoLoaded && (
        <Image
          src={poster}
          alt="Banner Video Poster"
          fill
          priority={priority}
          loader={customImageLoader}
          sizes="100vw"
          quality={90}
          style={{
            objectFit: 'cover',
          }}
        />
      )}

      {/* Video sau khi đã tải (chỉ hiển thị trên desktop) */}
      {isVideoLoaded && (
        <Box
          ref={videoRef}
          component={'video'}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={poster}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: { xs: 'relative', xl: 'absolute' },
            top: 0,
            left: 0,
            display: 'block',
          }}
        >
          <source src={video} type="video/mp4" />
          Your browser does not support the video tag.
        </Box>
      )}
    </Box>
  )
}
